import type { AssertLooselyAssignable, Url } from "@solomon-labs/types";
import type { Environment } from "@solomon-labs/types-enums/environment";

import rawConfig from "./config.json" with { type: "json" };

type EffectiveEnvironments = Exclude<Environment, "Test">;

type SolanaNetwork = "mainnet-beta" | "devnet" | "testnet";

interface ApiUrls {
    solomonData: Url;
    yaas: Url;
    yaasMetrics: Url;
    rewards: Url;
}

interface AppEnvironmentConfig {
    rpcEndpoint: Url;
    network: SolanaNetwork;
    apiUrls: ApiUrls;
    verificationWallet: string;
}

interface EnvironmentConfig<Env extends EffectiveEnvironments = EffectiveEnvironments> {
    config: AppEnvironmentConfig;
    environment: Env;
}

interface AppConfig {
    name: string;
    shortName: string;
    version: string;
    environments: Record<EffectiveEnvironments, EnvironmentConfig>;
}

type App = "main"; // e.g. | "yaas";

interface RawConfig {
    appHint: App;
    apps: {
        rewards: AppConfig;
        data: AppConfig;
    };
}

// This widens but works well enough, since it will complain otherwise
type _TypeChecked = AssertLooselyAssignable<RawConfig, typeof rawConfig>;

declare global {
    // keep this if you don’t want Node types

    var process:
        | undefined
        | {
              env?: {
                  APP?: string;
              };
          };
}

const hasWindow = typeof window !== "undefined";
const hostname = hasWindow ? window.location.hostname : "";
// eslint-disable-next-line @typescript-eslint/prefer-optional-chain
const appEnv = typeof process !== "undefined" && process && process.env ? process.env.APP : undefined;

const app: App = "main";
const PRODUCTION_HOSTNAMES = ["app.solomonlabs.org", "yaas.solomonlabs.org"];
const environment = PRODUCTION_HOSTNAMES.includes(hostname)
    ? ("Production" as EffectiveEnvironments)
    : ("Development" as EffectiveEnvironments);

export const { name: APP_NAME, shortName: APP_SHORT_NAME, version: APP_VERSION } = rawConfig.apps[app];
export const APP_CONFIG = rawConfig.apps[app].environments[environment] as EnvironmentConfig;

// Solana connection config
export const RPC_ENDPOINT = APP_CONFIG.config.rpcEndpoint as Url;
export const SOLANA_NETWORK = APP_CONFIG.config.network as SolanaNetwork;

// Verification wallet address (for wallet ownership proof)
export const VERIFICATION_WALLET_ADDRESS = APP_CONFIG.config.verificationWallet;

// API URLs (environment-aware)
export const API_URLS = {
    SOLOMON_DATA: APP_CONFIG.config.apiUrls.solomonData as Url,
    YAAS: APP_CONFIG.config.apiUrls.yaas as Url,
    YAAS_METRICS: APP_CONFIG.config.apiUrls.yaasMetrics as Url,
    REWARDS: APP_CONFIG.config.apiUrls.rewards as Url
} as const;

export { solomonTailwindExtendTheme, solomonTailwindFontStacks, solomonTailwindThemeTokens } from "./tailwind/theme";
